import { DuoMeasurementValues, MeasureEntity, MeasureSettings } from "@novorender/api";
import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadonlyVec3, vec3 } from "gl-matrix";

import { type RootState } from "app";
import { DeepMutable, Picker, renderActions, resetView, selectBookmark, setViewpoint } from "features/render";
import type { ViewpointExtendedMeasureEntity } from "features/viewpoints/types";
import { ExtendedMeasureEntity } from "types/misc";

import { SnapKind } from "./config";

export type SelectedMeasureObj = {
    id: number;
    pos: vec3;
    settings?: MeasureSettings;
};

type MeasureEntityWithConnection = MeasureEntity & {
    connectionPoint?: ReadonlyVec3;
};
type MutableMeasureEntityWithConnection = DeepMutable<MeasureEntityWithConnection>;
type MutableExtendedMeasureEntity = DeepMutable<ExtendedMeasureEntity>;

export type ActiveAxis = {
    x: boolean;
    y: boolean;
    z: boolean;
    planar: boolean;
    result: boolean;
    normal: boolean;
};

/**
 * State for the object-to-object measurement ({@link Picker.ObjectMeasurement}): the shortest line
 * between two whole objects, without having to pick individual entities on them first.
 * Kept separate from `selectedEntities` because there are no entities involved - the API takes two
 * object ids and returns the result directly.
 */
export type ObjectMeasureState = {
    /** Objects picked so far, in pick order. At most 2. */
    objectIds: number[];
    result: DuoMeasurementValues | undefined;
    loading: boolean;
    /** Both objects are picked, but no connection was found. See `measureObjectToObject` for why. */
    noResult: boolean;
};

const initialObjectMeasureState: ObjectMeasureState = {
    objectIds: [],
    result: undefined,
    loading: false,
    noResult: false,
};

const initialState = {
    selectedEntities: [[]] as MutableExtendedMeasureEntity[][],
    hover: undefined as MutableMeasureEntityWithConnection | undefined,
    snapKind: "all" as SnapKind,
    pinned: undefined as undefined | number,
    duoMeasurementValues: [] as (
        | undefined
        | {
              result: DuoMeasurementValues;
              id: number;
              settings?: MeasureSettings;
          }
    )[],
    activeAxis: [] as (ActiveAxis | undefined)[],
    loadingBrep: false,
    currentIndex: 0 as number,
    visible: true,
    objectMeasure: initialObjectMeasureState as ObjectMeasureState,
    /**
     * Snapshot of `objectMeasure` captured at the start of the current edit session, used to revert
     * an in-session measurement on cancel. `null` when no edit session is active.
     */
    objectMeasureEditSessionStartSnapshot: null as ObjectMeasureState | null,
    /**
     * Object under the mouse while picking for the object-to-object measurement, highlighted to
     * show what a click would pick. Kept out of `objectMeasure` on purpose: hovering is not part of
     * the measurement, so it must not end up in the edit-session snapshot or on the undo stack.
     */
    objectMeasureHoverObjectId: null as number | null,
};

type State = typeof initialState;

function isLegacy(
    measureSet: MutableExtendedMeasureEntity[] | MutableExtendedMeasureEntity[][],
): measureSet is MutableExtendedMeasureEntity[] {
    return measureSet.length > 0 && !Array.isArray(measureSet[0]);
}

function areMeasureEntitiesEqual(entity1: ExtendedMeasureEntity, entity2: ExtendedMeasureEntity): boolean {
    return (
        entity1.ObjectId === entity2.ObjectId &&
        entity1.instanceIndex === entity2.instanceIndex &&
        entity1.pathIndex === entity2.pathIndex &&
        entity1.drawKind === entity2.drawKind
    );
}

export const measureSlice = createSlice({
    name: "measure",
    initialState: initialState,
    reducers: {
        newMeasurement: (state) => {
            const entities = state.selectedEntities.at(-1);
            if (entities && entities.length > 0) {
                if (entities.length > 1 || entities[0].drawKind !== "vertex") {
                    state.selectedEntities.push([]);
                }
            }
            state.pinned = undefined;
            state.currentIndex = state.selectedEntities.length - 1;
        },
        /** Always starts a new measurement set, regardless of entity type. */
        forceNewMeasureSet: (state) => {
            const lastSet = state.selectedEntities.at(-1);
            if (lastSet && lastSet.length > 0) {
                state.selectedEntities.push([]);
            }
            state.pinned = undefined;
            state.currentIndex = state.selectedEntities.length - 1;
        },
        selectEntity: (state, action: PayloadAction<{ entity: ExtendedMeasureEntity; pin?: boolean }>) => {
            state.hover = undefined;
            const selectIdx = [1, undefined].includes(state.pinned) ? 0 : 1;

            const currentEntities = state.selectedEntities[state.currentIndex];
            const planeMeasure = action.payload.entity?.settings?.planeMeasure;
            state.activeAxis[state.currentIndex] = {
                x: true,
                y: true,
                z: planeMeasure ? false : true,
                planar: planeMeasure ? false : true,
                result: true,
                normal: true,
            };
            currentEntities[selectIdx] = action.payload.entity as MutableExtendedMeasureEntity;
            if (action.payload.pin) {
                state.pinned = selectIdx;
            }

            state.selectedEntities[state.currentIndex] = currentEntities;
        },
        unselectEntity: (state, action: PayloadAction<ExtendedMeasureEntity>) => {
            const currentEntities = state.selectedEntities[state.currentIndex];
            const entityIndex = currentEntities.findIndex((entity) => areMeasureEntitiesEqual(entity, action.payload));

            if (entityIndex !== -1) {
                currentEntities.splice(entityIndex, 1);
                state.selectedEntities[state.currentIndex] = currentEntities;

                if (state.pinned === entityIndex) {
                    state.pinned = undefined;
                } else if (state.pinned !== undefined && state.pinned > entityIndex) {
                    state.pinned = state.pinned - 1;
                }

                state.hover = undefined;
            }
        },
        selectMeasureSet: (state, action: PayloadAction<number>) => {
            state.currentIndex = action.payload;
        },
        deleteMeasureSet: (state, action: PayloadAction<number>) => {
            if (state.selectedEntities.length > 1) {
                state.selectedEntities.splice(action.payload, 1);
                state.duoMeasurementValues.splice(action.payload, 1);
                state.activeAxis.splice(action.payload, 1);
            } else {
                state.selectedEntities = [[]];
                state.activeAxis = [];
                state.duoMeasurementValues = [];
            }
            state.currentIndex = state.selectedEntities.length - 1;
            state.pinned = undefined;
            state.hover = undefined;
        },
        deleteEntityFromSet: (state, action: PayloadAction<{ setIdx: number; entityIdx: number }>) => {
            const { setIdx, entityIdx } = action.payload;
            const set = state.selectedEntities[setIdx];

            if (!set) {
                return;
            }

            set.splice(entityIdx, 1);

            if (set.length === 0) {
                if (state.selectedEntities.length > 1) {
                    state.selectedEntities.splice(setIdx, 1);
                    state.duoMeasurementValues.splice(setIdx, 1);
                    state.activeAxis.splice(setIdx, 1);
                } else {
                    state.selectedEntities = [[]];
                    state.activeAxis = [];
                    state.duoMeasurementValues = [];
                }
                state.currentIndex = state.selectedEntities.length - 1;
            }

            state.pinned = undefined;
            state.hover = undefined;
        },
        /**
         * Removes several entities, possibly spread across several sets, at once. Indices refer to
         * the current state, so callers don't have to account for the shifting that repeated
         * `deleteEntityFromSet` dispatches cause.
         */
        deleteEntitiesFromSets: (state, action: PayloadAction<{ setIdx: number; entityIdx: number }[]>) => {
            const entityIndicesBySetIdx = new Map<number, Set<number>>();
            for (const { setIdx, entityIdx } of action.payload) {
                if (!state.selectedEntities[setIdx]) {
                    continue;
                }
                const entityIndices = entityIndicesBySetIdx.get(setIdx) ?? new Set<number>();
                entityIndices.add(entityIdx);
                entityIndicesBySetIdx.set(setIdx, entityIndices);
            }

            if (entityIndicesBySetIdx.size === 0) {
                return;
            }

            const emptiedSetIndices: number[] = [];
            for (const [setIdx, entityIndices] of entityIndicesBySetIdx) {
                const set = state.selectedEntities[setIdx].filter((_, entityIdx) => !entityIndices.has(entityIdx));
                state.selectedEntities[setIdx] = set;
                if (set.length === 0) {
                    emptiedSetIndices.push(setIdx);
                }
            }

            if (emptiedSetIndices.length === state.selectedEntities.length) {
                state.selectedEntities = [[]];
                state.activeAxis = [];
                state.duoMeasurementValues = [];
            } else {
                // Descending, so that the indices left to remove stay valid
                for (const setIdx of emptiedSetIndices.sort((a, b) => b - a)) {
                    state.selectedEntities.splice(setIdx, 1);
                    state.duoMeasurementValues.splice(setIdx, 1);
                    state.activeAxis.splice(setIdx, 1);
                }
            }

            if (emptiedSetIndices.length > 0) {
                state.currentIndex = state.selectedEntities.length - 1;
            }

            state.pinned = undefined;
            state.hover = undefined;
        },
        setMeasureSet: (state, action: PayloadAction<{ idx: number; entities: ExtendedMeasureEntity[] }>) => {
            const { idx, entities } = action.payload;

            // Ensure the array is large enough
            while (state.selectedEntities.length <= idx) {
                state.selectedEntities.push([]);
            }

            state.selectedEntities[idx] = entities as MutableExtendedMeasureEntity[];

            // Ensure activeAxis is also updated if needed (basic default)
            if (!state.activeAxis[idx]) {
                state.activeAxis[idx] = {
                    x: true,
                    y: true,
                    z: true,
                    planar: true,
                    result: true,
                    normal: true,
                };
            }

            state.currentIndex = idx;
        },
        removeAxis: (
            state,
            action: PayloadAction<{ axis: "x" | "y" | "z" | "dist" | "plan" | "normal"; idx: number }>,
        ) => {
            const res = state.activeAxis[action.payload.idx];
            if (res) {
                switch (action.payload.axis) {
                    case "x":
                        res.x = false;
                        break;
                    case "y":
                        res.y = false;
                        break;
                    case "z":
                        res.z = false;
                        break;
                    case "dist":
                        res.result = false;
                        break;
                    case "plan":
                        res.planar = false;
                        break;
                    case "normal":
                        res.normal = false;
                        break;
                }
                if (
                    (res.x === false &&
                        res.y === false &&
                        res.z === false &&
                        res.result === false &&
                        res.planar === false) ||
                    res.normal === false
                ) {
                    if (state.selectedEntities.length > 1) {
                        state.selectedEntities.splice(action.payload.idx, 1);
                        state.duoMeasurementValues.splice(action.payload.idx, 1);
                        state.activeAxis.splice(action.payload.idx, 1);
                    } else {
                        state.selectedEntities = [[]];
                        state.activeAxis = [];
                        state.duoMeasurementValues = [];
                    }
                    state.pinned = undefined;
                    state.currentIndex = state.selectedEntities.length - 1;
                } else {
                    state.activeAxis[action.payload.idx] = res;
                }
            }
        },
        selectHoverObj: (state, action: PayloadAction<MeasureEntityWithConnection | undefined>) => {
            state.hover = action.payload as MutableMeasureEntityWithConnection | undefined;
        },
        selectPickSettings: (state, action: PayloadAction<SnapKind>) => {
            state.snapKind = action.payload;
        },
        //Legacy
        setSelectedEntities: (state, action: PayloadAction<ExtendedMeasureEntity[]>) => {
            state.selectedEntities = [action.payload as MutableExtendedMeasureEntity[]];
            state.currentIndex = 0;
            state.pinned = undefined;
        },

        addMeasureEntites: (state, action: PayloadAction<ExtendedMeasureEntity[]>) => {
            state.selectedEntities.push(action.payload as MutableExtendedMeasureEntity[]);
        },

        pin: (state, action: PayloadAction<State["pinned"]>) => {
            state.pinned = action.payload;
        },
        unPin: (state) => {
            state.pinned = undefined;
        },
        clear: (state) => {
            state.selectedEntities = [[]];
            state.pinned = undefined;
            state.hover = undefined;
            state.currentIndex = 0;
            state.objectMeasure = initialObjectMeasureState;
            // Otherwise cancelling a later edit session would bring the cleared measurement back.
            state.objectMeasureEditSessionStartSnapshot = null;
        },
        setDuoMeasurementValues: (
            state,
            action: PayloadAction<
                (undefined | { result: DuoMeasurementValues; id: number; settings?: MeasureSettings })[]
            >,
        ) => {
            state.duoMeasurementValues = action.payload;
            for (let i = state.activeAxis.length; i < state.duoMeasurementValues.length; ++i) {
                const planeMeasure = state.duoMeasurementValues[i]?.settings?.planeMeasure;
                state.activeAxis.push({
                    x: true,
                    y: true,
                    z: planeMeasure ? false : true,
                    planar: planeMeasure ? false : true,
                    result: true,
                    normal: true,
                });
            }
        },
        setSettings: (state, action: PayloadAction<{ idx: number; settings: MeasureSettings }>) => {
            state.selectedEntities[state.currentIndex] = state.selectedEntities[state.currentIndex].map((obj, idx) =>
                idx === action.payload.idx ? { ...obj, settings: action.payload.settings } : obj,
            );
        },
        setLoadingBrep: (state, action: PayloadAction<State["loadingBrep"]>) => {
            state.loadingBrep = action.payload;
        },
        setVisible: (state, action: PayloadAction<boolean>) => {
            state.visible = action.payload;
        },
        /**
         * Add an object to the object-to-object measurement. Picking the same object again unpicks
         * it. Once the pair is complete a pick only clears it, so the next measurement starts from
         * the click after - the canvas goes through `dismissObjectMeasure` for that, which also
         * turns off the x-ray and makes it undoable.
         */
        pickMeasureObject: (state, action: PayloadAction<number>) => {
            const objectId = action.payload;
            const { objectIds } = state.objectMeasure;

            // A click changes what is picked, and the pointer handler stops tracking the hover
            // once the pair is complete - so the id standing here can be from before that. Drop it
            // and let the next pointer move say what is under the cursor, rather than risk tinting
            // an object the mouse has long left.
            state.objectMeasureHoverObjectId = null;

            if (objectIds.length >= 2) {
                state.objectMeasure = initialObjectMeasureState;
                return;
            }

            state.objectMeasure = {
                ...initialObjectMeasureState,
                objectIds: objectIds.includes(objectId)
                    ? objectIds.filter((id) => id !== objectId)
                    : [...objectIds, objectId],
            };
        },
        setObjectMeasureLoading: (state, action: PayloadAction<boolean>) => {
            state.objectMeasure.loading = action.payload;
        },
        setObjectMeasureResult: (state, action: PayloadAction<DuoMeasurementValues | undefined>) => {
            state.objectMeasure.result = action.payload;
            state.objectMeasure.noResult = !action.payload;
            state.objectMeasure.loading = false;
        },
        setObjectMeasure: (state, action: PayloadAction<ObjectMeasureState>) => {
            state.objectMeasure = action.payload;
        },
        /**
         * The object the mouse is over while picking, or `null` when it is over nothing. Dispatched
         * on every pointer move, so it relies on immer treating an unchanged id as a no-op.
         */
        setObjectMeasureHoverObjectId: (state, action: PayloadAction<number | null>) => {
            state.objectMeasureHoverObjectId = action.payload;
        },
        /**
         * Drops the object-to-object measurement and its picks, without touching the edit-session
         * snapshot - cancelling the session afterwards should still restore whatever it held.
         */
        clearObjectMeasure: (state) => {
            state.objectMeasure = initialObjectMeasureState;
            state.objectMeasureHoverObjectId = null;
        },
        /** Idempotent: captures a snapshot only if none has been taken yet. */
        captureObjectMeasureEditSessionStartSnapshot: (state) => {
            if (state.objectMeasureEditSessionStartSnapshot) {
                return;
            }
            state.objectMeasureEditSessionStartSnapshot = state.objectMeasure as ObjectMeasureState;
        },
        clearObjectMeasureEditSessionStartSnapshot: (state) => {
            state.objectMeasureEditSessionStartSnapshot = null;
        },
        setObjectMeasureEditSessionStartSnapshot: (
            state,
            action: PayloadAction<State["objectMeasureEditSessionStartSnapshot"]>,
        ) => {
            state.objectMeasureEditSessionStartSnapshot = action.payload;
        },
        /**
         * If a snapshot exists, revert the object measurement to it and clear the snapshot, so that
         * cancelling drops a measurement made this session but keeps one saved in an earlier one.
         * No-op when no snapshot is active.
         */
        restoreObjectMeasureEditSessionStartSnapshot: (state) => {
            const snapshot = state.objectMeasureEditSessionStartSnapshot;
            if (!snapshot) {
                return;
            }
            state.objectMeasure = snapshot;
            state.objectMeasureEditSessionStartSnapshot = null;
        },
    },
    extraReducers(builder) {
        builder.addCase(selectBookmark, (state, action) => {
            if (isLegacy(action.payload.measurements.measure.entities)) {
                state.selectedEntities.push(
                    action.payload.measurements.measure.entities as MutableExtendedMeasureEntity[],
                );
            } else if (action.payload.measurements.measure.entities.length > 0) {
                state.selectedEntities = action.payload.measurements.measure
                    .entities as MutableExtendedMeasureEntity[][];
            }
            if (action.payload.measurements.measure.activeAxis) {
                state.activeAxis = action.payload.measurements.measure.activeAxis;
            }
        });
        builder.addCase(setViewpoint, (state, action) => {
            const measure = action.payload.measurements?.measure;
            const { publicIdsToObjectIds } = action.meta;

            // Above the partial-apply return on purpose: applying a viewpoint moves the camera,
            // so whatever the cursor was over is no longer under it - the same staleness a camera
            // drag causes - and that holds however little else the apply carries.
            state.objectMeasureHoverObjectId = null;

            if (action.meta.ignoreUndefined && !measure) {
                return;
            }

            // A viewpoint never carries the object-to-object measurement, so a full apply
            // has to clear it rather than leave the session's own behind. Below the return
            // above: a partial apply leaves out what it does not mention, and this is one
            // of the fields it would be leaving out.
            state.objectMeasure = initialObjectMeasureState;
            state.objectMeasureEditSessionStartSnapshot = null;

            if (!measure || measure.entities.length === 0) {
                state.selectedEntities = [[]];
                state.activeAxis = [];
                state.pinned = undefined;
                state.currentIndex = 0;
                return;
            }

            state.selectedEntities = measure.entities.map((set) =>
                set
                    .map((e) => convertViewpointEntity(e, publicIdsToObjectIds))
                    .filter((e): e is MutableExtendedMeasureEntity => e !== null),
            );
            state.activeAxis = measure.active_axis;
            state.currentIndex = state.selectedEntities.length - 1;
            state.pinned = undefined;
        });
        // Hovering belongs to the picking tool, so leaving it drops the hovered object instead of
        // leaving one behind to be highlighted the next time the tool is opened. Mirrors what
        // `renderSlice` does with the section tool's own hover.
        builder.addCase(renderActions.setPicker, (state, action) => {
            if (action.payload !== Picker.ObjectMeasurement) {
                state.objectMeasureHoverObjectId = null;
            }
        });
        builder.addCase(renderActions.stopPicker, (state, action) => {
            if (action.payload === Picker.ObjectMeasurement) {
                state.objectMeasureHoverObjectId = null;
            }
        });
        builder.addCase(resetView, (state) => {
            state.selectedEntities = [[]];
            state.pinned = undefined;
            state.hover = undefined;
            state.currentIndex = 0;
            state.objectMeasure = initialObjectMeasureState;
            // Otherwise cancelling a later edit session would bring the wiped measurement back.
            state.objectMeasureEditSessionStartSnapshot = null;
            // A reset leaves the picker alone, so the tool can still be open - without this the
            // hover would keep highlighting whatever was under the cursor before the jump.
            state.objectMeasureHoverObjectId = null;
        });
    },
});

function convertViewpointEntity(
    e: ViewpointExtendedMeasureEntity,
    publicIdsToObjectIds: Map<string, number>,
): ExtendedMeasureEntity | null {
    if (e.object_id === undefined) {
        return null;
    }
    const objectId = publicIdsToObjectIds.get(e.object_id);
    if (objectId === undefined) {
        return null;
    }
    const settings: MeasureSettings | undefined = e.settings
        ? {
              cylinderMeasure: e.settings.cylinder_measure,
              segmentLabelInterval: e.settings.segment_label_interval,
              planeMeasure: e.settings.plane_measure,
          }
        : undefined;

    if (e.draw_kind === "vertex") {
        return {
            ObjectId: objectId,
            drawKind: "vertex",
            pathIndex: e.path_index,
            instanceIndex: e.instance_index,
            parameter: e.parameter,
            connectionPoint: e.connection_point,
            isGenerated: e.is_generated,
            settings,
        };
    }

    return {
        ObjectId: objectId,
        drawKind: e.draw_kind,
        kind: e.kind,
        pathIndex: e.path_index,
        instanceIndex: e.instance_index,
        parameter: e.parameter,
        tangent: e.tangent,
        normal: e.normal,
        curvePart: e.curve_part,
        connectionPoint: e.connection_point,
        isGenerated: e.is_generated,
        settings,
    };
}

export const selectMeasure = (state: RootState) =>
    state.measure as Omit<State, "selectedEntities" | "hover"> & {
        selectedEntities: ExtendedMeasureEntity[][];
        hover: ExtendedMeasureEntity | undefined;
    };
export const selectHoveredMeasureEntity = (state: RootState) => state.measure.hover;
export const selectMeasureEntities = (state: RootState) => state.measure.selectedEntities as ExtendedMeasureEntity[][];
export const selectMeasureVisible = (state: RootState) => state.measure.visible;
export const selectObjectMeasure = (state: RootState) => state.measure.objectMeasure;
export const selectObjectMeasureIds = (state: RootState) => state.measure.objectMeasure.objectIds;
export const selectObjectMeasureResult = (state: RootState) => state.measure.objectMeasure.result;
export const selectObjectMeasureEditSessionStartSnapshot = (state: RootState) =>
    state.measure.objectMeasureEditSessionStartSnapshot;
export const selectObjectMeasureHoverObjectId = (state: RootState) => state.measure.objectMeasureHoverObjectId;

const selectEffectiveSnapKind = (state: RootState) => {
    switch (state.render.picker) {
        case Picker.Area:
            return "point";
        case Picker.PointLine:
        case Picker.Coordinates:
        case Picker.SdkPicking:
            return "pointAndCurve";
        case Picker.Dimension:
            return "surface";
        default:
            return state.measure.snapKind;
    }
};

// Base tolerance for default snap is point=0.015.
// For particular snap kinds - just multiply point by ~2-3 (???)
// How we came up with that:
// 1. Select a point with measure tool
// 2. Move camera close to this point so you're ~1m away from it
// 3. Use line measure tool to define your tolerance radius
export const selectMeasureHoverSettings = createSelector(selectEffectiveSnapKind, (snapKind) => {
    switch (snapKind) {
        case "point":
            return { point: 0.03 };
        case "curve":
            return { edge: 0.03, segment: 0.02 };
        case "pointAndCurve":
            return { point: 0.03, edge: 0.03, segment: 0.02, edgeMidpoint: 0.03, arcCenter: 0.05 };
        case "surface":
            return { face: 0.03 };
        default:
            return { edge: 0.008, segment: 0.008, face: 0.015, point: 0.015 };
    }
});
export const selectMeasurePickSettings = createSelector(selectEffectiveSnapKind, (snapKind) => {
    switch (snapKind) {
        case "point":
            return { point: 0.03 };
        case "curve":
            return { edge: 0.03, segment: 0.02 };
        case "pointAndCurve":
            return { point: 0.03, edge: 0.03, segment: 0.02, edgeMidpoint: 0.03, arcCenter: 0.05 };
        case "surface":
            return { face: 0.03 };
        default:
            return { edge: 0.008, segment: 0.008, face: 0.015, point: 0.015 };
    }
});

const { actions, reducer } = measureSlice;
export { actions as measureActions, reducer as measureReducer };
